import React from 'react';
import * as Tone from 'tone';
import { createYin } from './dsp/yin';
import { createHpsDetector } from './dsp/hps';
import { openMicStream, type AudioBlock, type StreamHandle } from './audioStream';
import { hzToMidi, hzToName, midiToHz, midiToName } from './util/note';
import type {
  AudioRecAPI,
  AudioRecBridgeTuning,
  AudioRecCalibrationProfile,
  AudioRecCalibrationState,
  AudioRecRecognitionHints,
  AudioRecListener,
  RecognitionFrame,
  RecognitionPeak,
} from './types';
import { CALIBRATION_STEPS } from './calibration/steps';
import { buildCalibrationProfile, sanitizeCalibrationProfile } from './calibration/profile';
import { countFrames, clamp, median, percentile } from './calibration/stats';
import type {
  CalibrationCaptures,
  CalibrationStep,
  CalibrationStepKind,
  StepCapture,
} from './calibration/types';
import { emptyCapture } from './calibration/types';
import { applyCalibrationProfileBias } from './recognition/profileMatcher';
import {
  centsBetween,
  harmonicNumberFor,
  isApproxHarmonic,
  isSamePitchHz,
} from './recognition/pitch';
import {
  clearActiveCalibrationProfileFromApi,
  fetchActiveCalibrationProfileFromApi,
  saveCalibrationProfileToApi,
} from './persistence/profileApi';
import type { DetectionWorkerFrame, DetectionWorkerResponse } from './worker/types';

interface AudioRecognitionDebugWindow extends Window {
  __DEBUG_AUDIO_REC?: boolean;
}

interface AudioRecognitionPromptWindow extends Window {
  __ensureSamplerReady?: (forceRebuild?: boolean) => Promise<void>;
  __museflowSampler?: Tone.Sampler;
}

type Ctx = (AudioRecAPI & { running: boolean }) | null;
export const AudioRecognitionContext = React.createContext<Ctx>(null);

const CALIBRATION_STORAGE_KEY = 'audio-recognition-calibration-v1';
const ABSOLUTE_MIN_DETECTION_RMS = 0.0065;
const ABSOLUTE_MIN_KEEP_RMS = 0.0052;
const ABSOLUTE_MIN_CALIBRATION_CAPTURE_RMS = 0.0052;
const CALIBRATION_ALL_HZ_PEAK_COUNT = 8;
const CALIBRATION_STORED_PEAK_COUNT = 10;
const CALIBRATION_HEARD_NOTE_PEAK_COUNT = 6;
const CALIBRATION_EXPECTED_HARMONIC_MAX = 8;
const CALIBRATION_EXPECTED_HARMONIC_CENTS = 70;

const DEFAULT_BRIDGE_TUNING: AudioRecBridgeTuning = {
  onRmsBase: 0.0065,
  keepRmsBase: 0.0052,
  onStabilityMs: 120,
  offSilenceMs: 170,
  minHoldMs: 150,
  envAttackSec: 0.025,
  envReleaseSec: 0.18,
  keepGateRatio: 0.7,
};

interface DetectionTuning {
  noiseFloorBase: number;
  noiseFloorAlpha: number;
  minRmsMultiplier: number;
  minPeakAmpRatio: number;
  hpsPeakDrop: number;
  hpsHarmonics: number;
  hpsMinHz: number;
  hpsMaxHz: number;
  bridge: AudioRecBridgeTuning;
}

const DEFAULT_TUNING: DetectionTuning = {
  noiseFloorBase: 0.00005,
  noiseFloorAlpha: 0.05,
  minRmsMultiplier: 4.8,
  minPeakAmpRatio: 0.24,
  hpsPeakDrop: 4.8,
  hpsHarmonics: 4,
  hpsMinHz: 50,
  hpsMaxHz: 2000,
  bridge: DEFAULT_BRIDGE_TUNING,
};

const IDLE_CALIBRATION_STATE: AudioRecCalibrationState = {
  running: false,
  phase: 'idle',
  stageLabel: 'Ready',
  instruction: 'Run mic calibration to adapt to your piano and room.',
  detail:
    'We will count down before each capture and only move on after hearing the expected sound clearly.',
  message: 'Run mic calibration to adapt to your piano and room.',
  timerLabel: null,
  heardNotes: [],
  stepIndex: 0,
  totalSteps: 0,
  progress: 0,
  sampleCount: 0,
};

interface CalibrationSession {
  stepIndex: number;
  stepStartMs: number;
  retries: number;
  current: StepCapture;
  captures: CalibrationCaptures;
  resolve: () => void;
}

interface DetectorState {
  yin: ReturnType<typeof createYin> | null;
  hps: ReturnType<typeof createHpsDetector> | null;
  yinKey: string;
  hpsKey: string;
}

const FALLBACK_PROMPT_DURATION_SEC = 0.65;
const DEFAULT_PROMPT_VELOCITY = 0.68;

interface StablePrimaryState {
  activeHz: number | null;
  activeSinceMs: number;
  lastSignalMs: number;
  candidateHz: number | null;
  candidateSinceMs: number;
  candidateFrames: number;
}

interface DetectedFundamentalCandidate extends RecognitionPeak {
  directRatio: number;
  harmonicCount: number;
  highHarmonicCount: number;
  hasThirdHarmonic: boolean;
}

interface StableDetectedNote {
  hz: number;
  amp: number;
  score: number;
  lastSeenMs: number;
  firstSeenMs: number;
  seenThisFrame: boolean;
}

interface StableDetectedNotesState {
  notes: StableDetectedNote[];
}

interface CalibrationCaptureAssessment {
  valid: boolean;
  progress: number;
  detail: string;
  heardNotes: string[];
}

interface CalibrationApplyProfileOptions {
  publishState?: boolean;
  persist?: boolean;
  provisional?: boolean;
}

const emptyStablePrimaryState = (): StablePrimaryState => ({
  activeHz: null,
  activeSinceMs: 0,
  lastSignalMs: 0,
  candidateHz: null,
  candidateSinceMs: 0,
  candidateFrames: 0,
});

const emptyStableDetectedNotesState = (): StableDetectedNotesState => ({
  notes: [],
});

const MAX_DETECTED_HARMONIC = 6;

const rankRecognitionPeaks = (peaks: RecognitionPeak[]): RecognitionPeak[] => {
  if (peaks.length < 2) return peaks.slice();

  const strongestAmp = peaks.reduce((max, peak) => Math.max(max, peak.amp), 0);
  const minHz = peaks.reduce((min, peak) => Math.min(min, peak.hz), Infinity);
  const maxHz = peaks.reduce((max, peak) => Math.max(max, peak.hz), 0);
  const scoreValues = peaks
    .map((peak) => peak.score)
    .filter((score): score is number => score != null && Number.isFinite(score));
  const minScore = scoreValues.length > 0 ? Math.min(...scoreValues) : 0;
  const maxScore = scoreValues.length > 0 ? Math.max(...scoreValues) : 0;
  const scoreRange = Math.max(1e-6, maxScore - minScore);
  const logHzRange =
    minHz > 0 && maxHz > minHz ? Math.max(1e-6, Math.log(maxHz) - Math.log(minHz)) : 0;

  const peakRank = (peak: RecognitionPeak) => {
    const ampScore = strongestAmp > 0 ? peak.amp / strongestAmp : 0;
    const harmonicSupport = peaks.reduce((sum, other) => {
      if (other === peak || other.hz <= peak.hz) return sum;
      return isApproxHarmonic(other.hz, peak.hz)
        ? sum + other.amp / Math.max(strongestAmp, 1e-6)
        : sum;
    }, 0);
    const harmonicPenalty = peaks.reduce((maxPenalty, other) => {
      if (other === peak || other.hz >= peak.hz) return maxPenalty;
      if (!isApproxHarmonic(peak.hz, other.hz)) return maxPenalty;
      return Math.max(maxPenalty, other.amp / Math.max(strongestAmp, 1e-6));
    }, 0);
    const score =
      peak.score != null && Number.isFinite(peak.score) ? (peak.score - minScore) / scoreRange : 0;
    const lowBias = logHzRange > 0 ? 1 - (Math.log(peak.hz) - Math.log(minHz)) / logHzRange : 0;

    return (
      ampScore * 1.15 +
      harmonicSupport * 0.8 +
      score * 0.18 +
      lowBias * 0.14 -
      harmonicPenalty * 0.95
    );
  };

  return peaks.slice().sort((a, b) => peakRank(b) - peakRank(a) || b.amp - a.amp || a.hz - b.hz);
};

const harmonicSupportWeight = (harmonic: number) => {
  if (harmonic === 1) return 1.3;
  if (harmonic === 2) return 0.28;
  if (harmonic === 3) return 1.0;
  if (harmonic === 4) return 0.92;
  if (harmonic === 5) return 0.88;
  return 0.78;
};

const evaluateDetectedFundamentalCandidate = (
  candidateHz: number,
  peaks: RecognitionPeak[],
  strongestAmp: number,
): DetectedFundamentalCandidate | null => {
  if (!Number.isFinite(candidateHz) || candidateHz <= 0 || peaks.length === 0) return null;

  const strongestAmpSafe = Math.max(strongestAmp, 1e-6);
  let directAmp = 0;
  let weightedSupport = 0;
  let harmonicSupport = 0;
  let weightedHzSum = 0;
  let totalWeight = 0;
  const supportedHarmonics = new Set<number>();

  for (const peak of peaks) {
    const ratio = peak.hz / candidateHz;
    const harmonic = Math.round(ratio);
    if (harmonic < 1 || harmonic > MAX_DETECTED_HARMONIC) continue;

    const expectedHz = candidateHz * harmonic;
    const maxCents = harmonic === 1 ? 40 : 48;
    if (Math.abs(centsBetween(peak.hz, expectedHz)) > maxCents) continue;

    const normalizedAmp = peak.amp / strongestAmpSafe;
    const weight = harmonicSupportWeight(harmonic);
    const contribution = normalizedAmp * weight;

    weightedSupport += contribution;
    weightedHzSum += (peak.hz / harmonic) * contribution;
    totalWeight += contribution;
    supportedHarmonics.add(harmonic);

    if (harmonic === 1) {
      directAmp = Math.max(directAmp, peak.amp);
    } else {
      harmonicSupport += contribution;
    }
  }

  if (supportedHarmonics.size === 0) return null;

  const harmonicNumbers = Array.from(supportedHarmonics);
  const harmonicCount = harmonicNumbers.filter((harmonic) => harmonic > 1).length;
  const highHarmonicCount = harmonicNumbers.filter((harmonic) => harmonic >= 3).length;
  const directRatio = directAmp / strongestAmpSafe;
  const hasThirdHarmonic = supportedHarmonics.has(3);
  const hasClusterSupport =
    (hasThirdHarmonic && highHarmonicCount >= 2) ||
    (hasThirdHarmonic && harmonicCount >= 2 && harmonicSupport >= 0.4) ||
    harmonicCount >= 3 ||
    (directRatio >= 0.18 && highHarmonicCount >= 1);
  const hasStrongDirect = directRatio >= 0.35;

  if (!hasStrongDirect && !hasClusterSupport) return null;

  const resolvedHz = totalWeight > 0 ? weightedHzSum / totalWeight : candidateHz;
  const score =
    weightedSupport + harmonicCount * 0.08 + highHarmonicCount * 0.12 + directRatio * 0.18;
  const amp = Math.max(directAmp, strongestAmpSafe * Math.min(1, harmonicSupport * 0.82));

  return {
    hz: resolvedHz,
    amp,
    score,
    directRatio,
    harmonicCount,
    highHarmonicCount,
    hasThirdHarmonic,
  };
};

const buildDetectedFundamentalCandidates = (
  peaks: RecognitionPeak[],
  minHz: number,
  maxHz: number,
  maxNotes = 4,
): DetectedFundamentalCandidate[] => {
  if (peaks.length === 0) return [];

  const strongestAmp = peaks.reduce((max, peak) => Math.max(max, peak.amp), 0);
  const candidateHzs: number[] = [];
  const addCandidateHz = (hz: number) => {
    if (!Number.isFinite(hz) || hz < minHz || hz > maxHz) return;
    if (candidateHzs.some((existingHz) => isSamePitchHz(existingHz, hz, 35))) return;
    candidateHzs.push(hz);
  };

  for (const peak of peaks) {
    addCandidateHz(peak.hz);
    for (let harmonic = 2; harmonic <= MAX_DETECTED_HARMONIC; harmonic++) {
      addCandidateHz(peak.hz / harmonic);
    }
  }

  const candidates = candidateHzs
    .map((hz) => evaluateDetectedFundamentalCandidate(hz, peaks, strongestAmp))
    .filter((candidate): candidate is DetectedFundamentalCandidate => candidate != null)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0) || b.amp - a.amp || a.hz - b.hz);

  const detected: DetectedFundamentalCandidate[] = [];
  for (const candidate of candidates) {
    if (detected.some((existing) => isSamePitchHz(existing.hz, candidate.hz, 35))) continue;

    const coveredByLowerNote = detected.find(
      (existing) =>
        candidate.hz > existing.hz &&
        isApproxHarmonic(candidate.hz, existing.hz, MAX_DETECTED_HARMONIC, 35) &&
        (existing.score ?? 0) >= (candidate.score ?? 0) * 0.72,
    );
    if (coveredByLowerNote) continue;

    const weakSubharmonicAlias = detected.find(
      (existing) =>
        existing.hz > candidate.hz &&
        isApproxHarmonic(existing.hz, candidate.hz, MAX_DETECTED_HARMONIC, 35) &&
        (!candidate.hasThirdHarmonic || candidate.harmonicCount < 2) &&
        candidate.highHarmonicCount < 2 &&
        candidate.directRatio < 0.88 &&
        (existing.score ?? 0) >= (candidate.score ?? 0) * 0.9,
    );
    if (weakSubharmonicAlias) continue;

    detected.push(candidate);
    if (detected.length >= maxNotes) break;
  }

  return detected;
};

const findMatchingDetectedPeakIndex = (
  peaks: RecognitionPeak[],
  targetHz: number,
  usedIndexes: Set<number>,
) => {
  let bestIndex = -1;
  let bestDistance = Infinity;

  for (let i = 0; i < peaks.length; i++) {
    if (usedIndexes.has(i)) continue;
    const peak = peaks[i];
    if (!isSamePitchHz(peak.hz, targetHz, 40)) continue;

    const distance = Math.abs(centsBetween(peak.hz, targetHz));
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = i;
    }
  }

  return bestIndex;
};

const buildStableDetectedNotes = (
  state: StableDetectedNotesState,
  detectedPeaks: RecognitionPeak[],
  rawPeaks: RecognitionPeak[],
  nowMs: number,
  bridge: AudioRecBridgeTuning,
  maxNotes = 4,
) => {
  const carryMs = Math.round(clamp(bridge.onStabilityMs + 45, 90, 170));
  const next: StableDetectedNote[] = [];
  const usedDetectedIndexes = new Set<number>();

  for (const note of state.notes) {
    const matchIndex = findMatchingDetectedPeakIndex(detectedPeaks, note.hz, usedDetectedIndexes);
    if (matchIndex >= 0) {
      const match = detectedPeaks[matchIndex];
      usedDetectedIndexes.add(matchIndex);
      next.push({
        hz: match.hz,
        amp: match.amp,
        score: match.score ?? note.score,
        lastSeenMs: nowMs,
        firstSeenMs: note.firstSeenMs,
        seenThisFrame: true,
      });
      continue;
    }

    const harmonicSupport = rawPeaks.some((peak) => {
      if (isSamePitchHz(peak.hz, note.hz, 40)) return true;
      if (!isApproxHarmonic(peak.hz, note.hz, MAX_DETECTED_HARMONIC, 48)) return false;
      const harmonic = Math.round(peak.hz / note.hz);
      // Ignore octave-only support so a true C4 does not keep a phantom C3 alive.
      return harmonic >= 3;
    });
    const ageSinceSeenMs = nowMs - note.lastSeenMs;
    if (!harmonicSupport && ageSinceSeenMs > carryMs) continue;

    next.push({
      hz: note.hz,
      amp: harmonicSupport ? note.amp : note.amp * 0.92,
      score: harmonicSupport ? note.score : note.score * 0.96,
      lastSeenMs: note.lastSeenMs,
      firstSeenMs: note.firstSeenMs,
      seenThisFrame: false,
    });
  }

  for (let i = 0; i < detectedPeaks.length; i++) {
    if (usedDetectedIndexes.has(i)) continue;
    const peak = detectedPeaks[i];
    const explainedByHeldNote = next.find(
      (existing) =>
        existing.seenThisFrame &&
        peak.hz > existing.hz &&
        isApproxHarmonic(peak.hz, existing.hz, MAX_DETECTED_HARMONIC, 40),
    );
    if (explainedByHeldNote) continue;
    next.push({
      hz: peak.hz,
      amp: peak.amp,
      score: peak.score ?? 0,
      lastSeenMs: nowMs,
      firstSeenMs: nowMs,
      seenThisFrame: true,
    });
  }

  next.sort(
    (a, b) =>
      Number(b.seenThisFrame) - Number(a.seenThisFrame) ||
      b.score - a.score ||
      b.amp - a.amp ||
      a.hz - b.hz,
  );

  const deduped: StableDetectedNote[] = [];
  for (const note of next) {
    if (deduped.some((existing) => isSamePitchHz(existing.hz, note.hz, 35))) continue;
    deduped.push(note);
    if (deduped.length >= maxNotes) break;
  }

  state.notes = deduped;
  return deduped;
};

const buildStableDetectedHzs = (
  activeHz: number | null,
  peaks: StableDetectedNote[],
  activeSinceMs: number,
  nowMs: number,
  maxNotes = 4,
) => {
  if (!activeHz || maxNotes <= 0) return [];

  const primaryPeak = peaks.find((peak) => isSamePitchHz(peak.hz, activeHz));
  const primaryDetected = !!primaryPeak;
  const primaryAmp = primaryPeak?.amp ?? 0;
  const primaryScore = primaryPeak?.score ?? 0;

  const hzs = [activeHz];
  for (const peak of peaks) {
    if (hzs.length >= maxNotes) break;
    if (isSamePitchHz(peak.hz, activeHz)) continue;

    const arrivedLateMs = activeSinceMs > 0 ? peak.firstSeenMs - activeSinceMs : 0;
    const isLateArrival = activeSinceMs > 0 && arrivedLateMs > 120;
    const primaryDominates =
      primaryDetected && (primaryAmp >= peak.amp * 1.1 || primaryScore >= (peak.score ?? 0) * 1.12);

    // Suppress upper harmonics of the active note unless they are strong enough to look independent.
    if (
      peak.hz > activeHz &&
      isApproxHarmonic(peak.hz, activeHz) &&
      (isLateArrival ||
        (!peak.seenThisFrame && primaryDominates) ||
        !primaryDetected ||
        primaryAmp >= peak.amp * 1.25)
    ) {
      continue;
    }

    // Suppress weaker lower subharmonic aliases when the active note has stronger support.
    if (
      peak.hz < activeHz &&
      isApproxHarmonic(activeHz, peak.hz, MAX_DETECTED_HARMONIC, 40) &&
      primaryDetected &&
      (isLateArrival || primaryAmp >= peak.amp * 1.25 || primaryScore >= (peak.score ?? 0) * 1.18)
    ) {
      continue;
    }

    if (hzs.some((hz) => isSamePitchHz(hz, peak.hz))) continue;
    hzs.push(peak.hz);
  }

  return hzs;
};

const updateStablePrimary = (
  state: StablePrimaryState,
  primaryHz: number | null,
  peaks: RecognitionPeak[],
  nowMs: number,
  bridge: AudioRecBridgeTuning,
) => {
  const clearCandidate = () => {
    state.candidateHz = null;
    state.candidateSinceMs = 0;
    state.candidateFrames = 0;
  };

  const setCandidate = (hz: number) => {
    state.candidateHz = hz;
    state.candidateSinceMs = nowMs;
    state.candidateFrames = 1;
  };

  const advanceCandidate = (hz: number) => {
    if (state.candidateHz != null && isSamePitchHz(state.candidateHz, hz)) {
      state.candidateHz = hz;
      state.candidateFrames += 1;
      return;
    }
    setCandidate(hz);
  };

  const confirmCandidate = () => {
    if (state.candidateHz == null) return;
    const candidateAgeMs = nowMs - state.candidateSinceMs;
    if (state.candidateFrames < 2 && candidateAgeMs < bridge.onStabilityMs) return;
    state.activeHz = state.candidateHz;
    state.activeSinceMs = nowMs;
    state.lastSignalMs = nowMs;
    clearCandidate();
  };

  const signalPresent = (primaryHz != null && Number.isFinite(primaryHz)) || peaks.length > 0;

  if (state.activeHz == null) {
    if (primaryHz == null || !signalPresent) {
      clearCandidate();
      return null;
    }
    advanceCandidate(primaryHz);
    confirmCandidate();
    return state.activeHz;
  }

  const activeHz = state.activeHz;
  const supportsActive =
    peaks.some((peak) => isSamePitchHz(peak.hz, activeHz)) ||
    (primaryHz != null && isSamePitchHz(primaryHz, activeHz));

  if (supportsActive) {
    state.lastSignalMs = nowMs;
    if (primaryHz != null && isSamePitchHz(primaryHz, activeHz)) {
      state.activeHz = primaryHz;
    }
    clearCandidate();
    return state.activeHz;
  }

  if (primaryHz != null && signalPresent) {
    advanceCandidate(primaryHz);
    const candidateAgeMs = nowMs - state.candidateSinceMs;
    const activeAgeMs = nowMs - state.activeSinceMs;
    const candidateConfirmed = state.candidateFrames >= 2 || candidateAgeMs >= bridge.onStabilityMs;
    if (candidateConfirmed && activeAgeMs >= bridge.minHoldMs) {
      state.activeHz = state.candidateHz;
      state.activeSinceMs = nowMs;
      state.lastSignalMs = nowMs;
      clearCandidate();
      return state.activeHz;
    }
  } else {
    clearCandidate();
  }

  if (!signalPresent && nowMs - state.lastSignalMs >= bridge.offSilenceMs) {
    state.activeHz = null;
    state.activeSinceMs = 0;
    state.lastSignalMs = 0;
    clearCandidate();
    return null;
  }

  return state.activeHz;
};

const downsampleFrame = (frame: Float32Array, maxPoints = 512) => {
  if (frame.length <= maxPoints) return frame.slice();
  const step = Math.max(1, Math.floor(frame.length / maxPoints));
  const size = Math.ceil(frame.length / step);
  const out = new Float32Array(size);
  let j = 0;
  for (let i = 0; i < frame.length; i += step) {
    out[j++] = frame[i];
  }
  return out;
};

const arraysEqual = (a: unknown[], b: unknown[]) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const calibrationPhaseFromKind = (kind: CalibrationStepKind): AudioRecCalibrationState['phase'] => {
  if (kind === 'noise') return 'noise';
  if (kind === 'single') return 'single';
  return 'chord';
};

const calibrationStepTotalMs = (step: CalibrationStep) => (step.prepMs ?? 0) + step.durationMs;

const formatTimerLabel = (remainingMs: number, suffix: string) =>
  `${Math.max(0, remainingMs / 1000).toFixed(1)}s ${suffix}`;

const formatCalibrationMessage = (
  stageLabel: string,
  instruction: string,
  detail: string,
  timerLabel: string | null,
) => [stageLabel, instruction, detail, timerLabel].filter(Boolean).join(' • ');

const buildCalibrationState = (
  overrides: Partial<AudioRecCalibrationState>,
): AudioRecCalibrationState => {
  const next = {
    ...IDLE_CALIBRATION_STATE,
    ...overrides,
    timerLabel: overrides.timerLabel ?? null,
    heardNotes: overrides.heardNotes ?? [],
  };

  return {
    ...next,
    message: formatCalibrationMessage(
      next.stageLabel,
      next.instruction,
      next.detail,
      next.timerLabel,
    ),
  };
};

const describeCalibrationTarget = (step: CalibrationStep) => {
  if (step.kind === 'noise') return 'room silence';
  if (step.expectedMidi && step.expectedMidi.length > 0) {
    return step.expectedMidi.map((midi) => midiToName(midi)).join(' + ');
  }
  if (step.kind === 'chord') return 'the chord clearly';
  return 'the requested note clearly';
};

const calibrationInstructionDetail = (step: CalibrationStep) => {
  if (step.kind === 'noise') {
    return 'Keep your hands off the piano so we can sample room noise first.';
  }
  if (step.expectedMidi && step.expectedMidi.length > 0) {
    return `Listen to the example tone, then play ${describeCalibrationTarget(step)} clearly so we can hear it.`;
  }
  if (step.kind === 'single') {
    return step.promptMidi?.length
      ? 'Listen to the example note, then play one note in that register only and hold it steadily.'
      : 'Play one note only, hold it steadily, and avoid extra notes around it.';
  }
  return step.promptMidi?.length
    ? 'Listen to the example chord, then hold a similar interval or chord steadily until the capture finishes.'
    : 'Hold the interval or chord steadily until the capture finishes.';
};

const profileTimestamp = (profile: AudioRecCalibrationProfile | null) => {
  if (!profile?.createdAtIso) return 0;
  const parsed = Date.parse(profile.createdAtIso);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isDebugAudioRecognitionEnabled = () =>
  typeof window !== 'undefined' &&
  (Boolean((window as AudioRecognitionDebugWindow).__DEBUG_AUDIO_REC) ||
    window.localStorage?.getItem('debug-audio-recognition') === 'true');

const frameRoundedMidis = (frame: StepCapture['peakFrames'][number]) => {
  const unique = new Set<number>();
  for (const peak of frame.peaks.slice(0, CALIBRATION_HEARD_NOTE_PEAK_COUNT)) {
    unique.add(Math.round(hzToMidi(peak.hz)));
  }
  return Array.from(unique).filter((midi) => midi >= 0 && midi <= 127);
};

const stepMatchesMidi = (step: CalibrationStep, midi: number, tolerance = 0.65) => {
  if (step.expectedMidi && step.expectedMidi.length > 0) {
    return step.expectedMidi.some((expected) => Math.abs(midi - expected) <= tolerance);
  }
  if (step.midiRange) {
    const [minMidi, maxMidi] = step.midiRange;
    return midi >= minMidi - tolerance && midi <= maxMidi + tolerance;
  }
  return true;
};

const expectedHarmonicNumberForStep = (step: CalibrationStep, peakHz: number) => {
  if (!step.expectedMidi || step.expectedMidi.length === 0) return null;

  for (const expectedMidi of step.expectedMidi) {
    const expectedHz = midiToHz(expectedMidi);
    const harmonicNumber = harmonicNumberFor(
      peakHz,
      expectedHz,
      CALIBRATION_EXPECTED_HARMONIC_CENTS,
    );
    if (harmonicNumber != null && harmonicNumber <= CALIBRATION_EXPECTED_HARMONIC_MAX) {
      return harmonicNumber;
    }
  }

  return null;
};

const stepMatchesPeak = (step: CalibrationStep, peakHz: number, midiTolerance = 0.65) => {
  if (step.expectedMidi && step.expectedMidi.length > 0) {
    return expectedHarmonicNumberForStep(step, peakHz) != null;
  }

  return stepMatchesMidi(step, hzToMidi(peakHz), midiTolerance);
};

const describeHarmonicTarget = (step: CalibrationStep, peakHz: number) => {
  if (!step.expectedMidi || step.expectedMidi.length === 0) return null;

  for (const expectedMidi of step.expectedMidi) {
    const expectedHz = midiToHz(expectedMidi);
    const harmonicNumber = harmonicNumberFor(
      peakHz,
      expectedHz,
      CALIBRATION_EXPECTED_HARMONIC_CENTS,
    );
    if (harmonicNumber != null && harmonicNumber <= CALIBRATION_EXPECTED_HARMONIC_MAX) {
      return harmonicNumber === 1
        ? midiToName(expectedMidi)
        : `${midiToName(expectedMidi)} harmonic ${harmonicNumber}`;
    }
  }

  return null;
};

const heardNotesFromCapture = (capture: StepCapture, includeMidi?: (midi: number) => boolean) => {
  const midiCounts = new Map<number, number>();
  for (const frame of capture.peakFrames) {
    for (const midi of frameRoundedMidis(frame)) {
      if (includeMidi && !includeMidi(midi)) continue;
      midiCounts.set(midi, (midiCounts.get(midi) ?? 0) + 1);
    }
  }
  return Array.from(midiCounts.entries())
    .sort((a, b) => b[1] - a[1] || a[0] - b[0])
    .filter(([, count]) => count >= 2)
    .slice(0, 3)
    .map(([midi]) => midiToName(midi));
};

const heardExpectedEvidenceFromCapture = (step: CalibrationStep, capture: StepCapture) => {
  const evidenceCounts = new Map<string, number>();
  for (const frame of capture.peakFrames) {
    for (const peak of frame.peaks.slice(0, CALIBRATION_HEARD_NOTE_PEAK_COUNT)) {
      const label = describeHarmonicTarget(step, peak.hz);
      if (!label) continue;
      evidenceCounts.set(label, (evidenceCounts.get(label) ?? 0) + 1);
    }
  }

  return Array.from(evidenceCounts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .filter(([, count]) => count >= 2)
    .slice(0, 3)
    .map(([label]) => label);
};

const noiseGateForCalibrationCapture = (captures: CalibrationCaptures) => {
  const noiseRmsValues = captures.noise.flatMap((capture) => capture.rms);
  const noiseReference =
    percentile(noiseRmsValues, 85) ??
    percentile(noiseRmsValues, 60) ??
    DEFAULT_TUNING.noiseFloorBase;

  return Math.max(
    ABSOLUTE_MIN_CALIBRATION_CAPTURE_RMS,
    noiseReference * 1.45,
    noiseReference + 0.001,
  );
};

const minimumValidationCaptureMs = (step: CalibrationStep) => {
  if (step.kind === 'noise') return Math.min(step.durationMs, 900);
  if (step.kind === 'single') return Math.min(step.durationMs, 650);
  return Math.min(step.durationMs, 1200);
};

const assessCalibrationCapture = (
  step: CalibrationStep,
  capture: StepCapture,
  captures: CalibrationCaptures,
): CalibrationCaptureAssessment => {
  const peakFrames = capture.peakFrames;
  const heardMidiFilter =
    step.kind === 'single' ? (midi: number) => stepMatchesMidi(step, midi, 0.75) : undefined;

  if (capture.rms.length < 4) {
    return {
      valid: false,
      progress: 0,
      detail:
        step.kind === 'noise'
          ? 'Listening for room noise...'
          : `Listening for ${describeCalibrationTarget(step)}...`,
      heardNotes: [],
    };
  }

  if (step.kind === 'noise') {
    const medianRms = median(capture.rms) ?? 0;
    const lowRms = percentile(capture.rms, 25) ?? medianRms;
    const highRms = percentile(capture.rms, 85) ?? medianRms;
    const stableUpperBound = Math.max(medianRms * 1.45, lowRms + 0.0012, medianRms + 0.0009);
    const burstThreshold = Math.max(medianRms * 2.6, highRms + 0.0018, medianRms + 0.003);
    const stableFrames = capture.rms.filter((value) => value <= stableUpperBound).length;
    const burstFrames = capture.rms.filter((value) => value >= burstThreshold).length;
    const targetFrames = Math.max(5, Math.ceil(capture.rms.length * 0.6));
    const maxBurstFrames = Math.max(1, Math.floor(capture.rms.length * 0.2));
    return {
      valid:
        capture.rms.length >= 6 && stableFrames >= targetFrames && burstFrames <= maxBurstFrames,
      progress: Math.min(
        clamp(stableFrames / Math.max(1, targetFrames), 0, 1),
        clamp(maxBurstFrames / Math.max(1, burstFrames || 1), 0, 1),
      ),
      detail:
        burstFrames > maxBurstFrames
          ? `Heard transient sound during the silence sample. Keep still for another pass.`
          : `Sampling room tone... ${stableFrames}/${targetFrames} steady noise frames.`,
      heardNotes: [],
    };
  }

  const rmsGate = noiseGateForCalibrationCapture(captures);
  const energeticFrames = peakFrames.filter(
    (_frame, index) => (capture.rms[index] ?? 0) >= rmsGate,
  );
  const energeticCapture: StepCapture = {
    ...capture,
    rms: energeticFrames.map((frame) => frame.rms),
    peakFrames: energeticFrames,
  };
  const heardNotes =
    step.expectedMidi && step.expectedMidi.length > 0
      ? heardExpectedEvidenceFromCapture(step, energeticCapture)
      : heardNotesFromCapture(energeticCapture, heardMidiFilter);

  if (energeticFrames.length < 3) {
    return {
      valid: false,
      progress: clamp(energeticFrames.length / 3, 0, 1),
      detail: `Waiting for ${describeCalibrationTarget(step)} above room noise.`,
      heardNotes,
    };
  }

  if (step.kind === 'single') {
    let matchedFrames = 0;
    let clearFrames = 0;
    let dominantFrames = 0;
    let directMatchedFrames = 0;
    const matchedHarmonics = new Set<number>();

    for (const frame of energeticFrames) {
      const strongestAmp = frame.peaks[0]?.amp ?? 0;
      const matchingPeakIndex = frame.peaks.findIndex((peak) => stepMatchesPeak(step, peak.hz));

      if (matchingPeakIndex === -1) continue;
      matchedFrames += 1;

      const matchingPeak = frame.peaks[matchingPeakIndex];
      const matchingHarmonic = expectedHarmonicNumberForStep(step, matchingPeak.hz);
      if (matchingHarmonic === 1) {
        directMatchedFrames += 1;
      } else if (matchingHarmonic != null) {
        matchedHarmonics.add(matchingHarmonic);
      }

      if (matchingPeakIndex <= 3 || (strongestAmp > 0 && matchingPeak.amp >= strongestAmp * 0.42)) {
        clearFrames += 1;
      }
      if (matchingPeakIndex <= 1 || (strongestAmp > 0 && matchingPeak.amp >= strongestAmp * 0.62)) {
        dominantFrames += 1;
      }
    }

    const requiredMatchedFrames = Math.max(2, Math.ceil(energeticFrames.length * 0.34));
    const requiredClearFrames = Math.max(1, Math.ceil(energeticFrames.length * 0.12));
    const harmonicEvidenceEnough =
      !step.expectedMidi ||
      step.expectedMidi.length === 0 ||
      directMatchedFrames > 0 ||
      matchedHarmonics.size >= 2;
    const earlyPass =
      harmonicEvidenceEnough &&
      energeticFrames.length >= 2 &&
      matchedFrames >= 2 &&
      clearFrames >= 1 &&
      dominantFrames >= 1;
    const valid =
      earlyPass ||
      (energeticFrames.length >= 2 &&
        harmonicEvidenceEnough &&
        matchedFrames >= requiredMatchedFrames &&
        clearFrames >= requiredClearFrames);
    const target = describeCalibrationTarget(step);
    const heardSummary = heardNotes.length > 0 ? heardNotes.join(', ') : 'nothing stable yet';
    const detail = valid
      ? `Clearly hearing ${target}. Strong frames ${clearFrames}/${requiredClearFrames}.`
      : `Waiting for clear ${target}. Matched ${matchedFrames}/${requiredMatchedFrames} frames, strong ${clearFrames}/${requiredClearFrames}. Heard ${heardSummary}.`;

    return {
      valid,
      progress: Math.min(
        clamp(matchedFrames / requiredMatchedFrames, 0, 1),
        clamp(clearFrames / requiredClearFrames, 0, 1),
      ),
      detail,
      heardNotes,
    };
  }

  const supportCounts = new Map<number, number>();
  let chordFrames = 0;
  for (const frame of energeticFrames) {
    const roundedMidis = frameRoundedMidis(frame);
    if (roundedMidis.length >= (step.minDistinctMidis ?? 2)) {
      chordFrames += 1;
    }
    for (const midi of roundedMidis) {
      supportCounts.set(midi, (supportCounts.get(midi) ?? 0) + 1);
    }
  }

  const supportedDistinct = Array.from(supportCounts.values()).filter((count) => count >= 3).length;
  const requiredChordFrames = Math.max(3, Math.ceil(energeticFrames.length * 0.3));
  const valid =
    energeticFrames.length >= 4 &&
    chordFrames >= requiredChordFrames &&
    supportedDistinct >= (step.minDistinctMidis ?? 2);

  return {
    valid,
    progress: Math.min(
      clamp(chordFrames / requiredChordFrames, 0, 1),
      clamp(supportedDistinct / Math.max(step.minDistinctMidis ?? 2, 1), 0, 1),
    ),
    detail: valid
      ? `Clearly hearing a stable chord.`
      : `Waiting for a stable chord. Chord frames ${chordFrames}/${requiredChordFrames}. Heard ${
          heardNotes.length > 0 ? heardNotes.join(', ') : 'no stable chord tones yet'
        }.`,
    heardNotes,
  };
};

export const useAudioRecognition = (): AudioRecAPI & { running: boolean } => {
  const ctx = React.useContext(AudioRecognitionContext);
  if (!ctx) throw new Error('useAudioRecognition must be used within AudioRecognitionProvider');
  return ctx;
};

export const AudioRecognitionProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const listeners = React.useRef<Map<string, AudioRecListener>>(new Map());
  const frameListeners = React.useRef(0);
  const waveformFrameListeners = React.useRef(0);

  const [running, setRunning] = React.useState(false);
  const [rmsSensitivity, setRmsSensitivityState] = React.useState(1.0);
  const rmsSensitivityRef = React.useRef(1.0);

  const [calibration, setCalibration] =
    React.useState<AudioRecCalibrationState>(IDLE_CALIBRATION_STATE);
  const [calibrationProfile, setCalibrationProfileState] =
    React.useState<AudioRecCalibrationProfile | null>(null);
  const calibrationProfileRef = React.useRef<AudioRecCalibrationProfile | null>(null);
  const provisionalCalibrationProfileRef = React.useRef<AudioRecCalibrationProfile | null>(null);
  const recognitionHintsRef = React.useRef<AudioRecRecognitionHints | null>(null);

  const streamRef = React.useRef<StreamHandle | null>(null);
  const workerRef = React.useRef<Worker | null>(null);
  const calibrationPromptSynthRef = React.useRef<Tone.PolySynth | null>(null);
  const latestWaveformRef = React.useRef<Float32Array | undefined>(undefined);
  const detectorsRef = React.useRef<DetectorState>({
    yin: null,
    hps: null,
    yinKey: '',
    hpsKey: '',
  });

  const tuningRef = React.useRef<DetectionTuning>({
    ...DEFAULT_TUNING,
    bridge: { ...DEFAULT_TUNING.bridge },
  });
  const noiseFloorRef = React.useRef(DEFAULT_TUNING.noiseFloorBase);
  const lastPeaksLogMsRef = React.useRef(0);
  const lastDetectedLogMsRef = React.useRef(0);
  const calibrationUiTickMsRef = React.useRef(0);

  const latestNotesRef = React.useRef<string[]>([]);
  const latestHzsRef = React.useRef<number[]>([]);
  const stablePrimaryStateRef = React.useRef<StablePrimaryState>(emptyStablePrimaryState());
  const stableDetectedNotesRef = React.useRef<StableDetectedNotesState>(
    emptyStableDetectedNotesState(),
  );

  const calibrationSessionRef = React.useRef<CalibrationSession | null>(null);

  const pushWorkerConfig = React.useCallback(() => {
    const worker = workerRef.current;
    if (!worker) return;
    worker.postMessage({
      type: 'configure',
      profile: provisionalCalibrationProfileRef.current ?? calibrationProfileRef.current,
      hints: recognitionHintsRef.current,
    });
  }, []);

  const addListener = React.useCallback((l: AudioRecListener) => {
    const prev = listeners.current.get(l.name);
    if (prev?.onFrame) frameListeners.current = Math.max(0, frameListeners.current - 1);
    if (prev?.onFrame && prev.wantsWaveform) {
      waveformFrameListeners.current = Math.max(0, waveformFrameListeners.current - 1);
    }
    listeners.current.delete(l.name);
    listeners.current.set(l.name, l);
    if (l.onFrame) {
      frameListeners.current += 1;
      if (l.wantsWaveform) {
        waveformFrameListeners.current += 1;
      }
    }
  }, []);

  const removeListener = React.useCallback((l: AudioRecListener) => {
    const prev = listeners.current.get(l.name);
    if (prev?.onFrame) frameListeners.current = Math.max(0, frameListeners.current - 1);
    if (prev?.onFrame && prev.wantsWaveform) {
      waveformFrameListeners.current = Math.max(0, waveformFrameListeners.current - 1);
    }
    listeners.current.delete(l.name);
  }, []);

  const clearListeners = React.useCallback(() => {
    listeners.current.clear();
    frameListeners.current = 0;
    waveformFrameListeners.current = 0;
  }, []);

  const setRmsSensitivity = React.useCallback((v: number) => {
    const clamped = Math.min(2.5, Math.max(0.5, v));
    rmsSensitivityRef.current = clamped;
    setRmsSensitivityState(clamped);
  }, []);

  const setRecognitionHints = React.useCallback(
    (hints: AudioRecRecognitionHints | null) => {
      const normalized = hints
        ? {
            mode: hints.mode ?? 'default',
            expectedMidiNotes: Array.from(
              new Set((hints.expectedMidiNotes ?? []).map((value) => Math.round(value))),
            )
              .filter((value) => value >= 0 && value <= 127)
              .sort((a, b) => a - b),
          }
        : null;
      recognitionHintsRef.current = normalized;
      pushWorkerConfig();
      if (isDebugAudioRecognitionEnabled()) {
        console.log('[AR][hints]', normalized);
      }
    },
    [pushWorkerConfig],
  );

  const resetDetectors = React.useCallback(() => {
    detectorsRef.current = {
      yin: null,
      hps: null,
      yinKey: '',
      hpsKey: '',
    };
  }, []);

  const applyCalibrationProfile = React.useCallback(
    (
      profile: AudioRecCalibrationProfile,
      {
        publishState = true,
        persist = true,
        provisional = false,
      }: CalibrationApplyProfileOptions = {},
    ) => {
      if (provisional) {
        provisionalCalibrationProfileRef.current = profile;
      } else {
        provisionalCalibrationProfileRef.current = null;
        calibrationProfileRef.current = profile;
      }

      if (publishState) {
        setCalibrationProfileState(profile);
      }

      tuningRef.current = {
        ...tuningRef.current,
        noiseFloorBase: profile.noiseFloorBase,
        minRmsMultiplier: profile.minRmsMultiplier,
        minPeakAmpRatio: profile.minPeakAmpRatio,
        hpsPeakDrop: profile.hpsPeakDrop,
        hpsHarmonics: profile.hpsHarmonics,
        hpsMinHz: profile.hpsMinHz,
        hpsMaxHz: profile.hpsMaxHz,
        bridge: { ...profile.bridge },
      };
      noiseFloorRef.current = profile.noiseFloorBase;
      setRmsSensitivity(profile.rmsSensitivity);
      pushWorkerConfig();

      if (!provisional) {
        resetDetectors();
      }

      if (!provisional && persist && typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(CALIBRATION_STORAGE_KEY, JSON.stringify(profile));
        } catch {
          // Ignore storage failures.
        }
      }
    },
    [pushWorkerConfig, resetDetectors, setRmsSensitivity],
  );

  const cancelCalibrationInternal = React.useCallback(
    (message: string) => {
      const session = calibrationSessionRef.current;
      if (!session) return;
      calibrationSessionRef.current = null;
      provisionalCalibrationProfileRef.current = null;
      pushWorkerConfig();
      session.resolve();

      setCalibration(
        buildCalibrationState({
          running: false,
          phase: 'cancelled',
          stageLabel: 'Cancelled',
          instruction: 'Calibration cancelled.',
          detail: message,
          message,
          timerLabel: null,
          heardNotes: [],
          stepIndex: 0,
          totalSteps: CALIBRATION_STEPS.length,
          progress: 0,
          sampleCount: 0,
        }),
      );
    },
    [pushWorkerConfig],
  );

  const clearCalibrationProfile = React.useCallback(() => {
    provisionalCalibrationProfileRef.current = null;
    calibrationProfileRef.current = null;
    setCalibrationProfileState(null);
    tuningRef.current = {
      ...DEFAULT_TUNING,
      bridge: { ...DEFAULT_BRIDGE_TUNING },
    };
    noiseFloorRef.current = DEFAULT_TUNING.noiseFloorBase;
    setRmsSensitivity(1.0);
    resetDetectors();
    pushWorkerConfig();

    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(CALIBRATION_STORAGE_KEY);
      } catch {
        // Ignore storage failures.
      }
    }

    void clearActiveCalibrationProfileFromApi();

    setCalibration({
      ...buildCalibrationState({
        ...IDLE_CALIBRATION_STATE,
        stageLabel: 'Ready',
        instruction: 'Calibration reset.',
        detail: 'Using the default detection profile.',
      }),
    });
  }, [pushWorkerConfig, resetDetectors, setRmsSensitivity]);

  const playCalibrationPrompt = React.useCallback(async (step: CalibrationStep) => {
    if (step.kind === 'noise') return;

    const promptMidis =
      step.promptMidi && step.promptMidi.length > 0
        ? step.promptMidi
        : step.expectedMidi && step.expectedMidi.length > 0
          ? step.expectedMidi
          : [];

    if (promptMidis.length === 0) return;

    const notes = promptMidis.map((midi) => midiToName(midi));
    const velocity = step.promptVelocity ?? DEFAULT_PROMPT_VELOCITY;

    try {
      await Tone.start();
      const promptWindow = window as AudioRecognitionPromptWindow;
      await promptWindow.__ensureSamplerReady?.();

      const when = Tone.now() + 0.04;
      const sampler = promptWindow.__museflowSampler;
      if (sampler) {
        notes.forEach((note) => {
          sampler.triggerAttackRelease(note, FALLBACK_PROMPT_DURATION_SEC, when, velocity);
        });
        return;
      }

      if (!calibrationPromptSynthRef.current) {
        calibrationPromptSynthRef.current = new Tone.PolySynth(Tone.Synth, {
          volume: -8,
          oscillator: { type: 'triangle' },
          envelope: {
            attack: 0.01,
            decay: 0.08,
            sustain: 0.45,
            release: 0.32,
          },
        }).toDestination();
      }

      calibrationPromptSynthRef.current.triggerAttackRelease(
        notes,
        FALLBACK_PROMPT_DURATION_SEC,
        when,
        velocity,
      );
    } catch (error) {
      console.warn('[AudioRecognition] Failed to play calibration prompt.', error);
    }
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(CALIBRATION_STORAGE_KEY);
      if (!raw) return;
      const parsed = sanitizeCalibrationProfile(JSON.parse(raw));
      if (!parsed) return;
      applyCalibrationProfile(parsed, false);
      setCalibration((prev) =>
        buildCalibrationState({
          ...prev,
          stageLabel: 'Ready',
          instruction: `Loaded saved mic profile (${parsed.label}).`,
          detail: 'This profile will be used for acoustic note recognition.',
        }),
      );
    } catch {
      // Ignore parse/storage errors.
    }
  }, [applyCalibrationProfile]);

  React.useEffect(() => {
    let cancelled = false;

    void fetchActiveCalibrationProfileFromApi().then((remoteProfile) => {
      if (cancelled || !remoteProfile) return;

      const currentProfile = calibrationProfileRef.current;
      if (profileTimestamp(remoteProfile) < profileTimestamp(currentProfile)) {
        return;
      }

      applyCalibrationProfile(remoteProfile, true);
      setCalibration((prev) =>
        buildCalibrationState({
          ...prev,
          stageLabel: 'Ready',
          instruction: `Loaded cloud mic profile (${remoteProfile.label}).`,
          detail: 'This profile is newer than the local one and is now active.',
        }),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [applyCalibrationProfile]);

  const ensureDetectors = React.useCallback((sampleRate: number, frameSize: number) => {
    const tuning = tuningRef.current;
    const yinKey = `${sampleRate}|${frameSize}`;
    if (!detectorsRef.current.yin || detectorsRef.current.yinKey !== yinKey) {
      detectorsRef.current.yin = createYin({
        sampleRate,
        bufferSize: frameSize,
        threshold: 0.12,
      });
      detectorsRef.current.yinKey = yinKey;
    }

    const hpsKey = [
      sampleRate,
      frameSize,
      tuning.hpsHarmonics,
      tuning.hpsPeakDrop,
      tuning.hpsMinHz,
      tuning.hpsMaxHz,
    ].join('|');
    if (!detectorsRef.current.hps || detectorsRef.current.hpsKey !== hpsKey) {
      detectorsRef.current.hps = createHpsDetector({
        sampleRate,
        bufferSize: frameSize,
        maxNotes: 4,
        minHz: tuning.hpsMinHz,
        maxHz: tuning.hpsMaxHz,
        harmonics: tuning.hpsHarmonics,
        peakDrop: tuning.hpsPeakDrop,
      });
      detectorsRef.current.hpsKey = hpsKey;
    }

    return {
      yin: detectorsRef.current.yin,
      hps: detectorsRef.current.hps,
    };
  }, []);

  const updateCalibrationUi = React.useCallback(
    (session: CalibrationSession, nowMs: number, force = false) => {
      if (!force && nowMs - calibrationUiTickMsRef.current < 120) return;
      calibrationUiTickMsRef.current = nowMs;

      const step = CALIBRATION_STEPS[session.stepIndex];
      if (!step) return;
      const elapsed = nowMs - session.stepStartMs;
      const prepMs = step.prepMs ?? 0;
      const totalMs = calibrationStepTotalMs(step);
      const stepProgress = clamp(elapsed / totalMs, 0, 1);
      const progress = clamp((session.stepIndex + stepProgress) / CALIBRATION_STEPS.length, 0, 1);
      const phase = calibrationPhaseFromKind(step.kind);
      const isCapturing = elapsed >= prepMs;
      const assessment = assessCalibrationCapture(step, session.current, session.captures);
      const remainingMs = isCapturing
        ? Math.max(0, step.durationMs - (elapsed - prepMs))
        : Math.max(0, prepMs - elapsed);
      const stageLabel = isCapturing ? 'Capture' : 'Get ready';
      const detail = isCapturing ? assessment.detail : calibrationInstructionDetail(step);

      setCalibration(
        buildCalibrationState({
          running: true,
          phase,
          stageLabel,
          instruction: step.label,
          detail,
          timerLabel: isCapturing ? formatTimerLabel(remainingMs, 'remaining') : null,
          heardNotes: assessment.heardNotes,
          stepIndex: session.stepIndex + 1,
          totalSteps: CALIBRATION_STEPS.length,
          progress,
          sampleCount: countFrames(session.captures, session.current),
        }),
      );
    },
    [],
  );

  const processCalibrationFrame = React.useCallback(
    (rms: number, rawPeaks: RecognitionPeak[], nowMs: number) => {
      const session = calibrationSessionRef.current;
      if (!session) return;

      const step = CALIBRATION_STEPS[session.stepIndex];
      if (!step) return;
      const prepMs = step.prepMs ?? 0;
      const elapsed = nowMs - session.stepStartMs;

      updateCalibrationUi(session, nowMs);

      if (elapsed < prepMs) return;

      const captureElapsed = elapsed - prepMs;

      const sortedPeaks = rawPeaks.slice().sort((a, b) => (b.amp ?? 0) - (a.amp ?? 0));
      const dominant = sortedPeaks[0];
      const second = sortedPeaks[1];

      session.current.rms.push(rms);
      session.current.peakCounts.push(sortedPeaks.length);
      if (dominant) {
        session.current.dominantHzs.push(dominant.hz);
        const maxHzs = sortedPeaks.slice(0, CALIBRATION_ALL_HZ_PEAK_COUNT).map((p) => p.hz);
        session.current.allHzs.push(...maxHzs);
      }
      session.current.peakFrames.push({
        rms,
        dominantHz: dominant?.hz ?? null,
        peaks: sortedPeaks.slice(0, CALIBRATION_STORED_PEAK_COUNT).map((peak) => ({
          hz: peak.hz,
          amp: peak.amp,
          score: peak.score,
        })),
      });
      if (dominant && second && dominant.amp > 0) {
        session.current.secondRatios.push(second.amp / dominant.amp);
      }
      const assessment = assessCalibrationCapture(step, session.current, session.captures);
      const hasMinimumCapture = captureElapsed >= minimumValidationCaptureMs(step);
      const hasFullCapture = captureElapsed >= step.durationMs;
      if (!hasMinimumCapture && !hasFullCapture) return;

      const valid = assessment.valid;
      if (!valid && !hasFullCapture) return;

      if (!valid) {
        session.retries += 1;
        session.current = emptyCapture();
        session.stepStartMs = nowMs;
        setCalibration(
          buildCalibrationState({
            running: true,
            phase: calibrationPhaseFromKind(step.kind),
            stageLabel: session.retries > 1 ? 'Still waiting' : 'Retry',
            instruction: step.label,
            detail:
              step.kind === 'noise'
                ? `Need a clean silence sample before moving on. ${assessment.detail}`
                : `Need to clearly hear ${describeCalibrationTarget(step)} before moving on. ${assessment.detail}`,
            timerLabel: null,
            heardNotes: assessment.heardNotes,
            stepIndex: session.stepIndex + 1,
            totalSteps: CALIBRATION_STEPS.length,
            progress: clamp(session.stepIndex / CALIBRATION_STEPS.length, 0, 1),
            sampleCount: countFrames(session.captures),
          }),
        );
        void playCalibrationPrompt(step);
        return;
      }

      session.captures[step.kind].push(session.current);
      try {
        const interimProfile = buildCalibrationProfile(
          session.captures,
          rmsSensitivityRef.current,
          {
            noiseFloorBase: DEFAULT_TUNING.noiseFloorBase,
            hpsMinHz: DEFAULT_TUNING.hpsMinHz,
            hpsMaxHz: DEFAULT_TUNING.hpsMaxHz,
          },
        );
        applyCalibrationProfile(interimProfile, {
          publishState: false,
          persist: false,
          provisional: true,
        });
      } catch {
        // Keep the previous tuning if a partial profile cannot be built cleanly yet.
      }
      session.current = emptyCapture();
      session.retries = 0;
      session.stepIndex += 1;
      session.stepStartMs = nowMs;

      if (session.stepIndex < CALIBRATION_STEPS.length) {
        updateCalibrationUi(session, nowMs, true);
        void playCalibrationPrompt(CALIBRATION_STEPS[session.stepIndex]);
        return;
      }

      setCalibration(
        buildCalibrationState({
          running: true,
          phase: 'analyzing',
          stageLabel: 'Analyzing',
          instruction: 'Analyzing note and chord captures.',
          detail: 'Building your room and piano profile now.',
          timerLabel: null,
          heardNotes: [],
          stepIndex: CALIBRATION_STEPS.length,
          totalSteps: CALIBRATION_STEPS.length,
          progress: 0.98,
          sampleCount: countFrames(session.captures),
        }),
      );

      const profile = buildCalibrationProfile(session.captures, rmsSensitivityRef.current, {
        noiseFloorBase: DEFAULT_TUNING.noiseFloorBase,
        hpsMinHz: DEFAULT_TUNING.hpsMinHz,
        hpsMaxHz: DEFAULT_TUNING.hpsMaxHz,
      });
      applyCalibrationProfile(profile, { publishState: true, persist: true, provisional: false });
      void saveCalibrationProfileToApi(profile);

      calibrationSessionRef.current = null;
      setCalibration(
        buildCalibrationState({
          running: false,
          phase: 'complete',
          stageLabel: 'Complete',
          instruction: `Calibration complete: ${profile.label}.`,
          detail: 'Your acoustic piano profile is ready to use.',
          timerLabel: null,
          heardNotes: [],
          stepIndex: CALIBRATION_STEPS.length,
          totalSteps: CALIBRATION_STEPS.length,
          progress: 1,
          sampleCount: countFrames(session.captures),
        }),
      );
      session.resolve();
    },
    [applyCalibrationProfile, playCalibrationPrompt, updateCalibrationUi],
  );

  const emitRecognitionFrame = React.useCallback(
    (
      rec: RecognitionFrame,
      detected?: {
        inferred?: Array<{ hz: number; score: number }>;
        active?: Array<{ hz: number; score: number }>;
        stable?: Array<{ hz: number }>;
      },
    ) => {
      const nowMs = rec.timeSec * 1000;
      const debugEnabled = isDebugAudioRecognitionEnabled();

      if (debugEnabled && rec.peaks && rec.peaks.length > 0) {
        const lastLog = lastPeaksLogMsRef.current;
        if (nowMs - lastLog > 120) {
          lastPeaksLogMsRef.current = nowMs;
          console.log(
            '[AR][peaks]',
            rec.peaks.map((peak) => ({
              note: hzToName(peak.hz),
              hz: Math.round(peak.hz * 100) / 100,
              amp: Math.round(peak.amp * 100000) / 100000,
            })),
          );
        }
      }

      if (
        debugEnabled &&
        detected &&
        ((detected.inferred?.length ?? 0) > 0 ||
          (detected.active?.length ?? 0) > 0 ||
          (detected.stable?.length ?? 0) > 0)
      ) {
        const lastLog = lastDetectedLogMsRef.current;
        if (nowMs - lastLog > 120) {
          lastDetectedLogMsRef.current = nowMs;
          console.log('[AR][detected]', {
            inferred: (detected.inferred ?? []).map((note) => ({
              note: hzToName(note.hz),
              hz: Math.round(note.hz * 100) / 100,
              score: Math.round(note.score * 1000) / 1000,
            })),
            held: (detected.active ?? []).map((note) => ({
              note: hzToName(note.hz),
              hz: Math.round(note.hz * 100) / 100,
              score: Math.round(note.score * 1000) / 1000,
            })),
            stable: (detected.stable ?? []).map((note) => ({
              note: hzToName(note.hz),
              hz: Math.round(note.hz * 100) / 100,
            })),
          });
        }
      }

      for (const [, listener] of listeners.current) {
        listener.onFrame?.(rec);
      }

      if (
        !arraysEqual(rec.notes, latestNotesRef.current) ||
        !arraysEqual(rec.hzs, latestHzsRef.current)
      ) {
        latestNotesRef.current = rec.notes.slice();
        latestHzsRef.current = rec.hzs.slice();

        if (debugEnabled) {
          console.log('[AR][noteChange]', {
            timeSec: rec.timeSec,
            hzs: rec.hzs,
            notes: rec.notes,
            rms: rec.rms,
          });
        }

        for (const [, listener] of listeners.current) {
          listener.onNoteChange?.(rec.notes, rec.hzs);
        }
      }
    },
    [],
  );

  const handleWorkerAnalysisFrame = React.useCallback(
    (frame: DetectionWorkerFrame) => {
      const nowMs = frame.timeSec * 1000;
      if (calibrationSessionRef.current) {
        processCalibrationFrame(frame.rms, frame.peaks, nowMs);
      }

      const detectedNotes = frame.stable.map((note) => ({
        ...note,
        note: midiToName(note.midi),
      }));
      const hzs = detectedNotes.map((note) => note.hz);
      const notes = detectedNotes.map((note) => note.note);
      const rec: RecognitionFrame = {
        timeSec: frame.timeSec,
        hz: hzs[0] ?? null,
        note: notes[0] ?? null,
        hzs,
        notes,
        midiNotes: frame.midiNotes.slice(),
        onsetMidiNotes: frame.onsetMidiNotes.slice(),
        detectedNotes,
        rms: frame.rms,
        minRms: frame.minRms,
        noiseFloor: frame.noiseFloor,
        peaks: frame.peaks,
        waveform: waveformFrameListeners.current > 0 ? latestWaveformRef.current : undefined,
      };

      emitRecognitionFrame(rec, {
        inferred: frame.inferred.map((note) => ({ hz: note.hz, score: note.score })),
        active: frame.active.map((note) => ({ hz: note.hz, score: note.score })),
        stable: frame.stable.map((note) => ({ hz: note.hz })),
      });
    },
    [emitRecognitionFrame, processCalibrationFrame],
  );

  const isRunning = React.useCallback(() => running, [running]);

  const stop = React.useCallback(async () => {
    streamRef.current?.stop();
    streamRef.current = null;
    workerRef.current?.terminate();
    workerRef.current = null;
    setRunning(false);
    latestNotesRef.current = [];
    latestHzsRef.current = [];
    latestWaveformRef.current = undefined;
    stablePrimaryStateRef.current = emptyStablePrimaryState();
    stableDetectedNotesRef.current = emptyStableDetectedNotesState();
    noiseFloorRef.current = tuningRef.current.noiseFloorBase;
    resetDetectors();
    if (calibrationSessionRef.current) {
      cancelCalibrationInternal('Calibration cancelled because microphone stopped.');
    }
  }, [cancelCalibrationInternal, resetDetectors]);

  const startLegacyStream = React.useCallback(async () => {
    if (streamRef.current) return;

    noiseFloorRef.current = tuningRef.current.noiseFloorBase;
    latestWaveformRef.current = undefined;
    stablePrimaryStateRef.current = emptyStablePrimaryState();
    stableDetectedNotesRef.current = emptyStableDetectedNotesState();

    const handle = await openMicStream((block: AudioBlock) => {
      const frame = block.samples;
      const sampleRate = block.sampleRate;
      latestWaveformRef.current =
        waveformFrameListeners.current > 0 ? downsampleFrame(frame, 512) : undefined;
      const { yin, hps } = ensureDetectors(sampleRate, frame.length);

      let sum = 0;
      for (let i = 0; i < frame.length; i++) {
        const v = frame[i];
        sum += v * v;
      }
      const rms = Math.sqrt(sum / frame.length);

      const tuning = tuningRef.current;
      let noiseFloor = noiseFloorRef.current;
      if (!Number.isFinite(noiseFloor)) noiseFloor = tuning.noiseFloorBase;
      if (rms < noiseFloor * 1.5) {
        noiseFloor = noiseFloor + tuning.noiseFloorAlpha * (rms - noiseFloor);
      }
      noiseFloor = Math.max(tuning.noiseFloorBase * 0.35, noiseFloor);
      noiseFloorRef.current = noiseFloor;

      const sensitivity = rmsSensitivityRef.current || 1.0;
      const minRms = Math.max(
        ABSOLUTE_MIN_DETECTION_RMS,
        tuning.noiseFloorBase,
        noiseFloor * tuning.minRmsMultiplier * sensitivity,
      );
      const hasActiveDetection = stablePrimaryStateRef.current.activeHz != null;
      const keepMinRms = Math.max(
        ABSOLUTE_MIN_KEEP_RMS,
        tuning.bridge.keepRmsBase,
        minRms * tuning.bridge.keepGateRatio,
      );
      const gateMinRms = hasActiveDetection ? Math.min(minRms, keepMinRms) : minRms;

      let hz: number | null = null;
      let hzs: number[] = [];
      let peaks: RecognitionPeak[] = [];
      let detectedPeaks: RecognitionPeak[] = [];
      let heldDetectedNotes: StableDetectedNote[] = [];
      let heldDetectedPeaks: RecognitionPeak[] = [];
      let stableHzs: number[] = [];
      let stableNotes: string[] = [];

      const nowSec = block.timeSec;
      const nowMs = nowSec * 1000;

      const needPeaks = rms >= gateMinRms || calibrationSessionRef.current != null;
      let rawPeaks: RecognitionPeak[] = [];
      if (needPeaks) {
        rawPeaks = hps.estimatePeaks(frame);
      }

      if (calibrationSessionRef.current) {
        processCalibrationFrame(rms, rawPeaks, nowMs);
      }

      if (rms >= gateMinRms) {
        if (rawPeaks.length > 0) {
          const strongestAmp = rawPeaks.reduce((max, p) => Math.max(max, p.amp), 0);
          const minAmp = strongestAmp * tuning.minPeakAmpRatio;
          peaks = rankRecognitionPeaks(
            rawPeaks.filter(
              (p) => p.amp >= minAmp && p.hz >= tuning.hpsMinHz && p.hz <= tuning.hpsMaxHz,
            ),
          );
        }

        detectedPeaks = buildDetectedFundamentalCandidates(
          peaks,
          tuning.hpsMinHz,
          tuning.hpsMaxHz,
          4,
        );
        detectedPeaks = applyCalibrationProfileBias(
          calibrationProfileRef.current,
          peaks,
          detectedPeaks,
          recognitionHintsRef.current,
          4,
        );
        heldDetectedNotes = buildStableDetectedNotes(
          stableDetectedNotesRef.current,
          detectedPeaks,
          peaks,
          nowMs,
          tuning.bridge,
          4,
        );
        heldDetectedPeaks = heldDetectedNotes.map((noteState) => ({
          hz: noteState.hz,
          amp: noteState.amp,
          score: noteState.score,
        }));
        hzs = heldDetectedPeaks.map((p) => p.hz);
        if (hzs.length === 0) {
          const est = yin.estimateHz(frame);
          if (est && Number.isFinite(est)) {
            hzs = [est];
            detectedPeaks = [{ hz: est, amp: 0, score: 0 }];
            heldDetectedNotes = [
              {
                hz: est,
                amp: 0,
                score: 0,
                lastSeenMs: nowMs,
                firstSeenMs: nowMs,
                seenThisFrame: true,
              },
            ];
            heldDetectedPeaks = [{ hz: est, amp: 0, score: 0 }];
          }
        }

        const primaryCandidateHz = detectedPeaks[0]?.hz ?? hzs[0];
        if (primaryCandidateHz != null) {
          hz = primaryCandidateHz;
        }
      }

      const stablePrimaryHz = updateStablePrimary(
        stablePrimaryStateRef.current,
        hz,
        detectedPeaks,
        nowMs,
        tuning.bridge,
      );
      stableHzs =
        stablePrimaryHz != null
          ? buildStableDetectedHzs(
              stablePrimaryHz,
              heldDetectedNotes,
              stablePrimaryStateRef.current.activeSinceMs,
              nowMs,
            )
          : [];
      stableNotes = stableHzs.map((h) => hzToName(h)).filter((n): n is string => !!n);
      const frameHz = stableHzs[0] ?? null;
      const frameNote = stableNotes[0] ?? null;

      const rec: RecognitionFrame = {
        timeSec: nowSec,
        hz: frameHz,
        note: frameNote,
        hzs: stableHzs,
        notes: stableNotes,
        midiNotes: stableHzs.map((value) => Math.round(hzToMidi(value))),
        rms,
        minRms: gateMinRms,
        noiseFloor,
        peaks,
        waveform: waveformFrameListeners.current > 0 ? latestWaveformRef.current : undefined,
      };

      emitRecognitionFrame(rec, {
        inferred: detectedPeaks.map((note) => ({ hz: note.hz, score: note.score ?? 0 })),
        active: heldDetectedPeaks.map((note) => ({ hz: note.hz, score: note.score ?? 0 })),
        stable: stableHzs.map((hz) => ({ hz })),
      });
    });

    streamRef.current = handle;
    setRunning(true);
  }, [emitRecognitionFrame, ensureDetectors, processCalibrationFrame]);

  const start = React.useCallback(async () => {
    if (streamRef.current) return;

    noiseFloorRef.current = tuningRef.current.noiseFloorBase;
    latestWaveformRef.current = undefined;
    stablePrimaryStateRef.current = emptyStablePrimaryState();
    stableDetectedNotesRef.current = emptyStableDetectedNotesState();

    let worker: Worker | null = null;
    try {
      worker = new Worker(new URL('./worker/audioRecognition.worker.ts', import.meta.url), {
        type: 'module',
      });
      workerRef.current = worker;
      worker.onmessage = (event: MessageEvent<DetectionWorkerResponse>) => {
        const data = event.data;
        if (!data) return;
        if (data.type === 'analysis') {
          handleWorkerAnalysisFrame(data.frame);
          return;
        }
        if (data.type === 'error') {
          console.warn('[AudioRecognition] Worker error:', data.message);
        }
      };
      worker.onerror = (event) => {
        console.warn('[AudioRecognition] Detection worker crashed.', event);
      };
      worker.postMessage({ type: 'reset' });
      pushWorkerConfig();

      const handle = await openMicStream((block: AudioBlock) => {
        latestWaveformRef.current =
          waveformFrameListeners.current > 0 ? downsampleFrame(block.samples, 512) : undefined;
        worker?.postMessage(
          {
            type: 'audio-block',
            samples: block.samples,
            sampleRate: block.sampleRate,
            timeSec: block.timeSec,
          },
          [block.samples.buffer],
        );
      });

      streamRef.current = handle;
      setRunning(true);
    } catch (error) {
      console.warn('[AudioRecognition] Falling back to legacy detector.', error);
      if (worker) {
        worker.terminate();
      }
      workerRef.current = null;
      await startLegacyStream();
    }
  }, [handleWorkerAnalysisFrame, pushWorkerConfig, startLegacyStream]);

  const startCalibration = React.useCallback(async () => {
    if (calibrationSessionRef.current) return;
    if (!streamRef.current) {
      try {
        await start();
      } catch (err) {
        setCalibration(
          buildCalibrationState({
            running: false,
            phase: 'error',
            stageLabel: 'Microphone Required',
            instruction: 'Microphone access is required to run calibration.',
            detail: 'Allow microphone access, then try again.',
            timerLabel: null,
            heardNotes: [],
            stepIndex: 0,
            totalSteps: CALIBRATION_STEPS.length,
            progress: 0,
            sampleCount: 0,
          }),
        );
        throw err;
      }
    }

    await new Promise<void>((resolve) => {
      provisionalCalibrationProfileRef.current = null;
      pushWorkerConfig();
      const nowMs = (streamRef.current?.context.currentTime ?? 0) * 1000;
      const session: CalibrationSession = {
        stepIndex: 0,
        stepStartMs: nowMs,
        retries: 0,
        current: emptyCapture(),
        captures: { noise: [], single: [], chord: [] },
        resolve,
      };
      calibrationSessionRef.current = session;
      calibrationUiTickMsRef.current = 0;

      const firstStep = CALIBRATION_STEPS[0];
      setCalibration(
        buildCalibrationState({
          running: true,
          phase: calibrationPhaseFromKind(firstStep.kind),
          stageLabel: 'Get ready',
          instruction: firstStep.label,
          detail: calibrationInstructionDetail(firstStep),
          timerLabel: null,
          heardNotes: [],
          stepIndex: 1,
          totalSteps: CALIBRATION_STEPS.length,
          progress: 0,
          sampleCount: 0,
        }),
      );
      void playCalibrationPrompt(firstStep);
    });
  }, [playCalibrationPrompt, pushWorkerConfig, start]);

  const cancelCalibration = React.useCallback(() => {
    cancelCalibrationInternal('Calibration cancelled.');
  }, [cancelCalibrationInternal]);

  React.useEffect(() => {
    return () => {
      streamRef.current?.stop();
      streamRef.current = null;
      workerRef.current?.terminate();
      workerRef.current = null;
      try {
        calibrationPromptSynthRef.current?.dispose();
      } catch {
        // intentionally empty
      }
      calibrationPromptSynthRef.current = null;
      calibrationSessionRef.current?.resolve();
      calibrationSessionRef.current = null;
    };
  }, []);

  const value = React.useMemo(
    () => ({
      addListener,
      removeListener,
      clearListeners,
      isRunning,
      start,
      stop,
      running,
      rmsSensitivity,
      setRmsSensitivity,
      calibration,
      calibrationProfile,
      setRecognitionHints,
      startCalibration,
      cancelCalibration,
      clearCalibrationProfile,
    }),
    [
      addListener,
      removeListener,
      clearListeners,
      isRunning,
      start,
      stop,
      running,
      rmsSensitivity,
      setRmsSensitivity,
      calibration,
      calibrationProfile,
      setRecognitionHints,
      startCalibration,
      cancelCalibration,
      clearCalibrationProfile,
    ],
  );

  return (
    <AudioRecognitionContext.Provider value={value}>{children}</AudioRecognitionContext.Provider>
  );
};
